import {
  Alert,
  Box,
  Chip,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CallMadeIcon from "@mui/icons-material/CallMade";
import CallReceivedIcon from "@mui/icons-material/CallReceived";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type {
  CallContact,
  CallEndReason,
  CallLogEntry,
} from "@shared/types/foyer";
import { foyerApi } from "../http/foyer";
import TableSkeleton from "./TableSkeleton";
import { useFormat } from "../i18n/format";
import { translateKey, type Translate } from "../i18n/translate";

/**
 * Care → Video calls · Call log (VC-25).
 *
 * The read-only answer to "who looked into room 214 on Tuesday?" - a family
 * member, a care manager or a regulator will ask it, and this table is where it
 * is answered. It is READ-ONLY on purpose: it renders the audit trail, and an
 * audit trail you can edit is not evidence. There is no add, no delete, no
 * inline edit anywhere on this surface.
 *
 * It shows THAT a call happened - when, which room, which way, who, how long,
 * answered or not - and never WHAT was said. There is no column for content
 * because there is no content anywhere in the system to put in one (VC-16); the
 * rows are projected from audit events that never held a recording.
 *
 * Filters mirror the three questions the log is actually asked: which room,
 * which day(s), which contact. Each change refetches server-side, so the client
 * never holds more of the trail than it is showing.
 */

interface Props {
  premiseId: string;
}

/** The chip colour each end reason earns; its label is `callLogViewer.reason.*`. */
const REASON_COLOR: Record<
  CallEndReason,
  "success" | "default" | "warning" | "error"
> = {
  answered_then_hangup: "success",
  declined: "default",
  timeout: "warning",
  busy: "warning",
  policy_block: "default",
  error: "error",
};

const NO_VALUE = "-";

/** Seconds → "3m 04s" / "48s" / "-". A call that never connected reads "-". */
function formatDuration(entry: CallLogEntry): string {
  if (!entry.answered || entry.durationSec <= 0) return NO_VALUE;
  const m = Math.floor(entry.durationSec / 60);
  const s = entry.durationSec % 60;
  return m > 0 ? `${m}m ${String(s).padStart(2, "0")}s` : `${s}s`;
}

/** Who placed the call, as far as the log can name them without disclosing PHI. */
function initiatorLabel(
  t: Translate,
  entry: CallLogEntry,
  contactsById: Map<string, CallContact>
): string {
  const { kind, contactId, userId } = entry.initiator;
  if (contactId) {
    const c = contactsById.get(contactId);
    if (c) {
      return c.relation
        ? t("care:callLogViewer.contactWithRelation", {
            name: c.displayName,
            relation: c.relation,
          })
        : c.displayName;
    }
    return t("care:callLogViewer.initiator.directoryContact");
  }
  if (userId) return t("care:callLogViewer.initiator.staff");
  if (kind === "room") return t("care:callLogViewer.initiator.unit");
  return kind === "staff"
    ? t("care:callLogViewer.initiator.staff")
    : t("care:callLogViewer.initiator.phone");
}

export default function CallLogViewer({ premiseId }: Props) {
  const { t } = useTranslation(["care", "common"]);
  const fmt = useFormat();
  const [entries, setEntries] = useState<CallLogEntry[] | null>(null);
  const [contacts, setContacts] = useState<CallContact[]>([]);
  const [truncated, setTruncated] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [roomId, setRoomId] = useState("");
  const [contactId, setContactId] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const contactsById = useMemo(
    () => new Map(contacts.map((c) => [c.id, c])),
    [contacts]
  );

  // The contact filter offers every contact the premise has ever had, revoked
  // included: a call from someone since removed still needs to be findable in
  // the log - that history does not disappear when the contact does.
  useEffect(() => {
    let alive = true;
    foyerApi
      .listCallContacts(premiseId, { includeRevoked: true })
      .then((list) => alive && setContacts(list))
      .catch(() => alive && setContacts([]));
    return () => {
      alive = false;
    };
  }, [premiseId]);

  const load = useCallback(async () => {
    setError(null);
    setEntries(null);
    try {
      // `to` is a date; make it inclusive of the whole day by pushing the
      // exclusive upper bound to the following midnight.
      const toExclusive = to ? isoEndOfDay(to) : undefined;
      const page = await foyerApi.listCallLog(premiseId, {
        roomId: roomId.trim() || undefined,
        contactId: contactId || undefined,
        from: from || undefined,
        to: toExclusive,
      });
      setEntries(page.entries);
      setTruncated(page.truncated);
    } catch {
      setError(t("callLogViewer.loadFailed"));
      setEntries([]);
    }
  }, [premiseId, roomId, contactId, from, to, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const formatWhen = (iso: string) =>
    fmt.dateTime(iso, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  const contactOption = (c: CallContact) => {
    const named = c.relation
      ? t("callLogViewer.contactWithRelation", {
          name: c.displayName,
          relation: c.relation,
        })
      : c.displayName;
    return c.revokedAt
      ? t("callLogViewer.contactRemoved", { name: named })
      : named;
  };

  return (
    <Stack spacing={2} data-testid="call-log">
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 760 }}>
        {t("callLogViewer.intro")}
      </Typography>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        useFlexGap
        flexWrap="wrap"
      >
        <TextField
          label={t("callLogViewer.filters.unit")}
          value={roomId}
          onChange={(e) => setRoomId(e.target.value)}
          size="small"
          sx={{ minWidth: 120 }}
          inputProps={{ "data-testid": "call-log-room" }}
        />
        <TextField
          select
          label={t("callLogViewer.filters.contact")}
          value={contactId}
          onChange={(e) => setContactId(e.target.value)}
          size="small"
          sx={{ minWidth: 200 }}
          SelectProps={{ native: false }}
          inputProps={{ "data-testid": "call-log-contact" }}
        >
          <MenuItem value="">{t("callLogViewer.filters.allContacts")}</MenuItem>
          {contacts.map((c) => (
            <MenuItem key={c.id} value={c.id}>
              {contactOption(c)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label={t("callLogViewer.filters.from")}
          type="date"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          size="small"
          InputLabelProps={{ shrink: true }}
          inputProps={{ "data-testid": "call-log-from" }}
        />
        <TextField
          label={t("callLogViewer.filters.to")}
          type="date"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          size="small"
          InputLabelProps={{ shrink: true }}
          inputProps={{ "data-testid": "call-log-to" }}
        />
      </Stack>

      {error && <Alert severity="error">{error}</Alert>}
      {truncated && (
        <Alert severity="info" data-testid="call-log-truncated">
          {t("callLogViewer.truncated")}
        </Alert>
      )}

      {entries === null ? (
        <TableSkeleton rows={5} />
      ) : entries.length === 0 ? (
        <Alert severity="info" data-testid="call-log-empty">
          {t("callLogViewer.empty")}
        </Alert>
      ) : (
        <Box sx={{ overflowX: "auto" }}>
          <Table size="small" data-testid="call-log-table">
            <TableHead>
              <TableRow>
                <TableCell>{t("callLogViewer.columns.when")}</TableCell>
                <TableCell>{t("callLogViewer.columns.unit")}</TableCell>
                <TableCell>{t("callLogViewer.columns.direction")}</TableCell>
                <TableCell>{t("callLogViewer.columns.contact")}</TableCell>
                <TableCell>{t("callLogViewer.columns.outcome")}</TableCell>
                <TableCell align="right">
                  {t("callLogViewer.columns.duration")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {entries.map((entry) => {
                const reasonLabel = translateKey(
                  t,
                  `care:callLogViewer.reason.${entry.reason}`,
                  { defaultValue: entry.reason }
                );
                const reasonColor = REASON_COLOR[entry.reason] ?? "default";
                const outbound = entry.direction === "outbound";
                return (
                  <TableRow key={entry.seq} data-testid="call-log-row">
                    <TableCell>{formatWhen(entry.at)}</TableCell>
                    <TableCell>{entry.roomId || NO_VALUE}</TableCell>
                    <TableCell>
                      <Tooltip
                        title={
                          outbound
                            ? t("callLogViewer.outboundHint")
                            : t("callLogViewer.inboundHint")
                        }
                      >
                        <Stack
                          direction="row"
                          spacing={0.5}
                          alignItems="center"
                        >
                          {outbound ? (
                            <CallMadeIcon fontSize="small" color="action" />
                          ) : (
                            <CallReceivedIcon fontSize="small" color="action" />
                          )}
                          <span>
                            {outbound
                              ? t("callLogViewer.outbound")
                              : t("callLogViewer.inbound")}
                          </span>
                        </Stack>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      {initiatorLabel(t, entry, contactsById)}
                      {entry.autoAnswer && (
                        <Chip
                          label={t("callLogViewer.auto")}
                          size="small"
                          color="warning"
                          variant="outlined"
                          sx={{ ml: 1 }}
                          data-testid="call-log-auto"
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={reasonLabel}
                        size="small"
                        color={reasonColor}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right">{formatDuration(entry)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      )}
    </Stack>
  );
}

/** A YYYY-MM-DD date string → the ISO instant at the START of the next day, so
 *  an inclusive "to yesterday" covers all of yesterday. */
function isoEndOfDay(date: string): string {
  const d = new Date(`${date}T00:00:00`);
  d.setDate(d.getDate() + 1);
  return d.toISOString();
}
